import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Trash2, Mail, Share, GitCompare, Archive, Maximize2, Minimize2, Paperclip, Upload, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { opsApi, money, pct } from "../../lib/api";
import AddLinesDialog from "./AddLinesDialog";
import CoStatusDialog from "./CoStatusDialog";
import FrsSyncDialog from "./FrsSyncDialog";
import FrameworkEmailDialog from "../../components/FrameworkEmailDialog";
import Hint from "@/components/Hint";

const STATUS_TONE = {
  draft: "border-slate-200 bg-slate-50 text-slate-600",
  submitted: "border-sky-200 bg-sky-50 text-sky-700",
  process_unapproved: "border-amber-200 bg-amber-50 text-amber-800",
  process_approved: "border-emerald-200 bg-emerald-50 text-emerald-700",
  revise_resubmit: "border-violet-200 bg-violet-50 text-violet-700",
  rejected: "border-rose-200 bg-rose-50 text-rose-700",
  void: "border-slate-200 bg-slate-100 text-slate-500",
};
const FRS_TONE = {
  not_reviewed: "border-rose-200 bg-rose-50 text-rose-700",
  synced: "border-emerald-200 bg-emerald-50 text-emerald-700",
  task_created: "border-amber-200 bg-amber-50 text-amber-800",
  not_applicable: "border-slate-200 bg-slate-50 text-slate-500",
  overridden: "border-violet-200 bg-violet-50 text-violet-700",
};
const FRS_LABEL = {
  not_reviewed: "FRS not reviewed", synced: "FRS synced",
  task_created: "FRS task raised", not_applicable: "No scope change",
  overridden: "FRS review overridden",
};
const REASONS = [["client_request", "Client request"], ["design_change", "Design change"],
                 ["field_condition", "Field condition"], ["error_omission", "Error or omission"],
                 ["value_engineering", "Value engineering"], ["other", "Other"]];

/** One status pill, same look as the job status pill: click to move the CO (every move still goes
 *  through CoStatusDialog). Email → Submitted stays automatic. */
function CoStatusPill({ co, onPick }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);
  const moves = (co.transitions || []).filter((t) => !t.status || co.can_edit !== false);
  return (
    <div className="relative" ref={box}>
      <button type="button" onClick={() => moves.length && setOpen((v) => !v)} data-testid="ops-co-status-badge" title={co.status_meaning}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_TONE[co.status]}`}>
        {co.status_label}{moves.length > 0 && <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="absolute left-0 top-8 z-40 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900" data-testid="ops-co-status-menu">
          {moves.map((tr) => (
            <button key={tr.status} type="button" onClick={() => { setOpen(false); onPick(tr); }} data-testid={`ops-co-to-${tr.status}`}
                    className="block w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800">
              <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{tr.label}</span>
              <span className="block text-[11px] text-slate-500">{tr.meaning}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The builder IS the proposal builder. Same screen as the log, one click away. */
export default function CoBuilder({ project, co, onChanged, canEdit, canOverrideMargin, focused = false, onToggleFocus = null }) {
  const [adding, setAdding] = useState(false);
  const [target, setTarget] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const fromCo = (c) => ({
    title: c.title || "", description: c.description || "", reason: c.reason || "other",
    gc_reference: c.gc_reference || "",
    schedule_impact_days: c.schedule_impact_days ?? "",
  });
  const [detail, setDetail] = useState(() => fromCo(co));
  useEffect(() => { setDetail(fromCo(co)); }, [co.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Details auto-save: text fields on blur, the reason on change. Only what actually changed is sent.
  const commit = async (next) => {
    const saved = fromCo(co);
    const diff = Object.fromEntries(Object.entries(next).filter(([k, v]) => String(v) !== String(saved[k])));
    if (!Object.keys(diff).length) return;
    if ("schedule_impact_days" in diff) {
      diff.schedule_impact_days = diff.schedule_impact_days === "" ? null : Number(diff.schedule_impact_days);
    }
    setSaving(true);
    try { onChanged(await opsApi.patchChangeOrder(project.id, co.id, diff)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save that"); }
    finally { setSaving(false); }
  };
  const field = (key, extra = {}) => ({
    value: detail[key], disabled: !co.can_edit,
    onChange: (e) => setDetail({ ...detail, [key]: e.target.value }),
    onBlur: () => commit(detail),
    ...extra,
  });

  const patchLine = async (lineId, fields) => {
    try { onChanged(await opsApi.coPatchLine(project.id, co.id, lineId, fields)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change that line"); }
  };

  const removeLine = async (lineId) => {
    try { onChanged(await opsApi.coDeleteLine(project.id, co.id, lineId)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove that line"); }
  };

  const backupDocs = (co.documents || []).filter((d) => (d.co_document_kind || "backup") === "backup");
  const [dropping, setDropping] = useState(false);
  const uploadBackup = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setDropping(true);
    try {
      const form = new FormData();
      list.forEach((f) => form.append("files", f));
      form.append("kind", "backup");
      const res = await opsApi.coUploadDocument(project.id, co.id, form);
      toast.success(`${list.length} file${list.length === 1 ? "" : "s"} added as backup`);
      onChanged(res.change_order);
    } catch { toast.error("Those files could not be attached"); }
    finally { setDropping(false); }
  };
  const removeBackup = async (doc) => {
    try { await opsApi.deleteDocument(doc.id); onChanged(await opsApi.changeOrder(project.id, co.id)); }
    catch { toast.error("That file could not be removed"); }
  };

  const emailProposal = async () => {
    setBusy(true);
    try {
      const d = await opsApi.emailDraft(project.id, "co_proposal", co.id);
      setDraft({ ...d, ref: { change_order_id: co.id, attach_proposal: true, kind: "change_order" },
                 attachments: [{ filename: `${co.number} Change Order Proposal.pdf` },
                               ...backupDocs.map((b) => ({ filename: b.file_name, document_id: b.id }))] });
    } catch { toast.error("Could not prepare that email"); }
    finally { setBusy(false); }
  };

  const t = co.totals || {};
  const frsState = co.frs_sync?.state || "not_reviewed";

  return (
    <div className="space-y-4" data-testid={`ops-co-builder-${co.number}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-slate-900">{co.number}</h3>
        {co.revision > 0 && (
          <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] text-violet-700">
            Rev {co.revision}
          </span>
        )}
        <CoStatusPill co={co} onPick={(tr) => setTarget(tr)} />
        <button onClick={() => canEdit !== false && setSyncing(true)}
                className={`rounded-full border px-2 py-0.5 text-[11px] ${FRS_TONE[frsState]}`}
                data-testid="ops-co-frs-chip" title={co.frs_sync?.note || ""}>
          {FRS_LABEL[frsState]}
        </button>
        {co.ageing?.days != null && (
          <span className={`rounded-full border px-2 py-0.5 text-[11px] ${
            co.ageing.tone === "red" ? "border-rose-200 bg-rose-50 text-rose-700"
              : co.ageing.tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-slate-200 bg-slate-50 text-slate-600"}`}
                data-testid="ops-co-ageing">
            {co.ageing.days} days outstanding
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-3 text-sm tabular-nums" data-testid="ops-co-value">
          {[["Cost", money(t.cost), "ops-co-hdr-cost"], ["Total", money(t.sell), "ops-co-hdr-total"], ["GM%", pct(t.margin_pct), "ops-co-hdr-gm"]].map(([k, v, tid]) => (
            <span key={k} className="inline-flex items-baseline gap-1" data-testid={tid}>
              <span className="text-xs text-slate-400">{k}</span>
              <span className="font-semibold text-slate-900">{v}</span>
            </span>
          ))}
        </span>
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" data-testid="ops-co-saving" />}
        {onToggleFocus && (
          <button type="button" onClick={onToggleFocus} data-testid={focused ? "ops-co-collapse" : "ops-co-expand"}
                  title={focused ? "Collapse back into the change order log" : "Open this change order on its own page"}
                  className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800 dark:border-slate-700 dark:hover:bg-slate-800">
            {focused ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {co.editable && co.can_edit && (
          <Button size="sm" onClick={() => setAdding(true)} data-testid="ops-co-add-lines">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add lines
          </Button>
        )}
        <TooltipProvider delayDuration={1000}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" className="text-xs" onClick={() => setSyncing(true)}
                      data-testid="ops-co-frs-review">
                <GitCompare className="mr-1.5 h-3.5 w-3.5" /> FRS review
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start" className="max-w-xs text-xs leading-relaxed" data-testid="ops-co-frs-review-tip">
              <p className="font-semibold">What FRS review does</p>
              <p className="mt-1">Compares this CO's lines against the Current FRS and lists every room/product quantity it adds, deducts or changes. Accept the rows to write an FRS revision stamped {co.number} (Jobs and RA update together), or raise an FRS Update task for the PE when lines have no room yet. A CO can't be Processed Approved until this review happens (or is overridden with a reason).</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button variant="data" size="sm" className="text-xs" onClick={emailProposal} disabled={busy}
                data-testid="ops-co-email">
          <Mail className="mr-1.5 h-3.5 w-3.5" /> CO
        </Button>
        <Button variant="data" size="sm" className="text-xs" data-testid="ops-co-proposal-pdf"
                onClick={() => opsApi.coProposalDownload(project.id, co.id, co.number)
                  .catch(() => toast.error("The proposal PDF could not be downloaded"))}>
          <Share className="mr-1.5 h-3.5 w-3.5" /> PDF
        </Button>
        <div className="ml-auto flex flex-wrap gap-2">
          {["rejected", "void"].includes(co.status) && (
            <Button variant="ghost" size="sm" data-testid="ops-co-archive"
                    onClick={async () => onChanged(
                      await opsApi.coArchive(project.id, co.id, !co.archived))}>
              <Archive className="mr-1.5 h-3.5 w-3.5" /> {co.archived ? "Unarchive" : "Archive"}
            </Button>
          )}
        </div>
      </div>

      {/* details */}
      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="text-xs text-slate-500">Title</label>
          <Input {...field("title")} className="mt-1 h-9 text-sm" data-testid="ops-co-title" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-slate-500">Description (goes to the GC)</label>
          <Textarea {...field("description")} rows={3} className="mt-1 text-sm" data-testid="ops-co-description" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Reason</label>
          <select value={detail.reason} disabled={!co.can_edit} data-testid="ops-co-reason"
                  onChange={(e) => { const next = { ...detail, reason: e.target.value }; setDetail(next); commit(next); }}
                  className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
            {REASONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500">GC reference</label>
          <Input {...field("gc_reference")} className="mt-1 h-9 text-sm" data-testid="ops-co-gc-reference" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Schedule impact (days)</label>
          <Input type="number" {...field("schedule_impact_days")} className="mt-1 h-9 text-sm"
                 data-testid="ops-co-schedule-impact" />
        </div>
        <div>
          <label className="text-xs text-slate-500"><Hint text="Read from the cost codes on this change order's lines">Scope(s) changed</Hint></label>
          <div className="mt-1 flex min-h-9 flex-wrap items-center gap-1" data-testid="ops-co-scopes">
            {(co.scopes || []).length === 0 && <span className="text-xs text-slate-400">No lines yet</span>}
            {(co.scopes || []).map((sc) => (
              <span key={sc} className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300">{sc}</span>
            ))}
          </div>
        </div>
      </div>

      {/* lines */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[900px] text-sm" data-testid="ops-co-lines">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Manufacturer</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Unit cost</th>
              <th className="px-3 py-2 text-right">Cost</th>
              <th className="px-3 py-2 text-right"><Hint text="Material gross margin %">Mat GM%</Hint></th>
              <th className="px-3 py-2 text-right"><Hint text="Labor gross margin %">Lab GM%</Hint></th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(co.lines || []).length === 0 && (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-slate-500">
                No lines yet — pull them off the estimate, the Current FRS, or an FRS change.
              </td></tr>
            )}
            {(co.lines || []).map((l) => (
              <tr key={l.id} className={l.needs_price ? "bg-amber-50/60 dark:bg-amber-500/10" : ""}
                  data-testid={`ops-co-line-${l.id}`}>
                <td className="px-3 py-2">{l.description || "—"}
                  {l.needs_price && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">no cost</span>}
                  {l.frs_targets?.length > 0 && (
                    <span className="ml-1 text-[10px] text-slate-500 dark:text-slate-400">
                      {l.frs_targets[0].room_number}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">{l.manufacturer || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                  {{ estimate_line: "Estimate", frs_item: "FRS", frs_delta: "FRS change",
                     manual: "Typed" }[l.source] || l.source}
                </td>
                <td className="px-3 py-2 text-right">
                  {co.can_edit ? (
                    <Input type="number" defaultValue={l.qty} className="h-7 w-20 text-right text-xs"
                           data-testid={`ops-co-line-qty-${l.id}`}
                           onBlur={(e) => Number(e.target.value) !== l.qty
                             && patchLine(l.id, { qty: Number(e.target.value) })} />
                  ) : <span className="tabular-nums">{l.qty}</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {co.can_edit ? (
                    <Input type="number" step="0.01" defaultValue={l.unit_cost}
                           className="h-7 w-24 text-right text-xs"
                           data-testid={`ops-co-line-cost-${l.id}`}
                           onBlur={(e) => Number(e.target.value) !== l.unit_cost
                             && patchLine(l.id, { unit_cost: Number(e.target.value) })} />
                  ) : <span className="tabular-nums">{money(l.unit_cost)}</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{money(l.cost)}</td>
                {[["material_gm", l.material_gm_used], ["labor_gm", l.labor_gm_used]].map(([field, used]) => (
                  <td key={field} className="px-3 py-2 text-right">
                    {canOverrideMargin && co.can_edit ? (
                      <Input type="number" step="0.01" defaultValue={used}
                             className={`h-7 w-20 text-right text-xs ${
                               l.margin_overridden ? "border-violet-300 bg-violet-50 dark:border-violet-600 dark:bg-violet-950/40" : ""}`}
                             data-testid={`ops-co-line-${field}-${l.id}`}
                             onBlur={(e) => Number(e.target.value) !== used
                               && patchLine(l.id, { [field]: Number(e.target.value) })} />
                    ) : <span className="tabular-nums">{pct(used)}</span>}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-medium tabular-nums">{money(l.sell)}</td>
                <td className="px-2 py-2 text-right">
                  {co.can_edit && (
                    <button onClick={() => removeLine(l.id)} data-testid={`ops-co-line-remove-${l.id}`}
                            className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-white font-semibold">
            <tr data-testid="ops-co-line-totals">
              <td className="px-3 py-2.5" colSpan={5}>
                {t.lines || 0} line(s) · {t.labor_hours || 0} hrs
                {t.margin_overrides ? ` · ${t.margin_overrides} margin override(s)` : ""}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums">{money(t.cost)}</td>
              <td className="px-3 py-2.5" colSpan={2} />
              <td className="px-3 py-2.5 text-right tabular-nums">{money(t.sell)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="text-xs text-slate-500" data-testid="ops-co-pricing-note">
        {co.pricing?.explanation}
      </p>

      {/* procurement prompt */}
      {co.procurement?.active && (
        <div className="space-y-2 rounded-xl border border-sky-200 bg-sky-50 p-4"
             data-testid="ops-co-procurement-prompt">
          <p className="text-sm font-semibold text-sky-900">Procurement</p>
          {co.procurement.prompts.map((p, i) => (
            <p key={i} className="text-xs text-sky-800" data-testid={`ops-co-procurement-${p.kind}`}>
              {p.message}
            </p>
          ))}
          <p className="text-[11px] text-sky-700/70">{co.procurement.note}</p>
        </div>
      )}

      {/* backup documents — ride along with the proposal email */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-co-backup">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Backup Documents</p>
          <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300">
            <Mail className="h-3 w-3" /> attached to the CO email with the proposal PDF
          </span>
        </div>
        <ul className="mt-2 space-y-1 text-xs text-slate-600" data-testid="ops-co-backup-list">
          {backupDocs.length === 0 && <li className="text-slate-400">No backup yet — drop quotes, photos or change documents here.</li>}
          {backupDocs.map((d) => (
            <li key={d.id} className="flex items-center gap-2" data-testid={`ops-co-backup-${d.id}`}>
              <Paperclip className="h-3 w-3 shrink-0 text-slate-400" />
              <button type="button" onClick={() => opsApi.openDocument(d.id).catch(() => toast.error("Could not open that file"))} className="truncate hover:underline">{d.file_name}</button>
              <span className="text-slate-400">{(d.uploaded_at || "").slice(0, 10)}</span>
              {canEdit !== false && (
                <button type="button" onClick={() => removeBackup(d)} className="ml-auto rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                        data-testid={`ops-co-backup-remove-${d.id}`} title="Remove"><Trash2 className="h-3 w-3" /></button>
              )}
            </li>
          ))}
        </ul>
        {canEdit !== false && (
          <label onDragOver={(e) => { e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); uploadBackup(e.dataTransfer.files); }}
                 className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-3 text-xs text-slate-600 hover:border-slate-400 dark:border-slate-600"
                 data-testid="ops-co-backup-drop">
            {dropping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Drop PDFs or photos here, or click to choose
            <input type="file" multiple accept=".pdf,image/*" className="hidden" data-testid="ops-co-backup-input"
                   onChange={(e) => { uploadBackup(e.target.files); e.target.value = ""; }} />
          </label>
        )}
      </div>

      {/* communications + history */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Communications</p>
          <ul className="mt-2 space-y-1.5 text-xs text-slate-600" data-testid="ops-co-communications">
            {(co.communications || []).length === 0 && <li className="text-slate-400">Nothing sent yet.</li>}
            {(co.communications || []).map((e) => (
              <li key={e.id}>
                {(e.at || "").slice(0, 16).replace("T", " ")} — {e.subject} → {(e.sent || []).join(", ")}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">History</p>
          <ul className="mt-2 space-y-1.5 text-xs text-slate-600" data-testid="ops-co-history">
            {(co.status_log || []).map((s) => (
              <li key={s.id}>
                {(s.at || "").slice(0, 16).replace("T", " ")} —{" "}
                {s.from_status ? `${s.from_status} → ` : ""}{s.to_status} · {s.by}
                {s.note ? ` · ${s.note}` : ""}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {adding && (
        <AddLinesDialog projectId={project.id} co={co} onClose={() => setAdding(false)}
                        onDone={onChanged} />
      )}
      {target && (
        <CoStatusDialog projectId={project.id} co={co} target={target}
                        onClose={() => setTarget(null)} onDone={onChanged} />
      )}
      {syncing && (
        <FrsSyncDialog projectId={project.id} co={co} onClose={() => setSyncing(false)}
                       onDone={onChanged} />
      )}
      {draft && (
        <FrameworkEmailDialog projectId={project.id} draft={draft} onClose={() => setDraft(null)}
                              onSent={() => opsApi.changeOrder(project.id, co.id).then(onChanged)} />
      )}
    </div>
  );
}
